import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "store";
import { PaymentMethodConfiguration } from "store/config";
import { selectAutoPay } from "store/payments/payments-slice";
import { accountEntityAdapter } from "./account-slice";
import { AccountMatrix, defaultAccountRestrictions } from "./account-state";
import { ACH, EWALLET, CARD } from "constants/common";

const accountTypeConfig = (state: RootState) =>
  state.config.paymentCenter.accountTypes;

export const selectedAccountInfo = (state: RootState) =>
  selectAccountById(state, state.accounts.currentSelectedAccount!);

export const activeAccountTypeConfig = createSelector(
  [selectedAccountInfo, accountTypeConfig],
  (currentAccount, config) => {
    const configArray = Object.values(config);
    return currentAccount?.accountType
      ? config[currentAccount.accountType]
      : configArray[0];
  },
);

export const getPaymentMethods = createSelector(
  [activeAccountTypeConfig],
  (accountTypes) => {
    let paymentMethods: { [key: string]: PaymentMethodConfiguration } = {};

    if (!!accountTypes) {
      for (const key in accountTypes.paymentMethods) {
        let method = accountTypes.paymentMethods[key];
        if (method.enabled) {
          if (key === EWALLET && Array.isArray(method.walletProviders)) {
            paymentMethods[key] = {
              ...method,
              walletProviders: method.walletProviders.filter(
                (provider) => provider.enabled,
              ),
            };
          } else {
            paymentMethods[key] = method;
          }
        }
      }
    }
    return paymentMethods;
  },
);

export const getAccountRestrictions = createSelector(
  [selectedAccountInfo, getPaymentMethods],
  (account, paymentMethods) => {
    if (!account)
      return {
        restrictions: defaultAccountRestrictions,
        accountMethods: Object.keys(paymentMethods),
      };

    let achRestricted = false;
    let cardRestricted = false;
    let eWalletRestricted = false;
    let accountPaymentMethods = Object.keys(paymentMethods);

    const isInactive = account.active !== true;
    const isReadOnly =
      (account.clientAttributes & AccountMatrix.ReadOnly) ===
      AccountMatrix.ReadOnly;

    if (isInactive || isReadOnly) {
      accountPaymentMethods = [];
    }

    if (ACH in paymentMethods) {
      achRestricted =
        (account.clientAttributes & AccountMatrix.EnableACH) !==
        AccountMatrix.EnableACH;
      if (achRestricted) {
        accountPaymentMethods = accountPaymentMethods.filter(
          (method) => method !== ACH,
        );
      }
    }

    if (CARD in paymentMethods) {
      cardRestricted =
        (account.clientAttributes & AccountMatrix.EnableCard) !==
        AccountMatrix.EnableCard;
      if (cardRestricted) {
        accountPaymentMethods = accountPaymentMethods.filter(
          (method) => method !== CARD,
        );
      }
    }

    if (EWALLET in paymentMethods) {
      eWalletRestricted =
        (account.clientAttributes & AccountMatrix.EnableEWallet) !==
        AccountMatrix.EnableEWallet;
      if (eWalletRestricted) {
        accountPaymentMethods = accountPaymentMethods.filter(
          (method) => method !== EWALLET,
        );
      }
    }

    return {
      restrictions: {
        isInactive,
        isReadOnly,
        achRestricted,
        cardRestricted,
        eWalletRestricted,
      },
      accountMethods: accountPaymentMethods,
    };
  },
);

const currentSelectedAccount = (state: RootState) =>
  state.accounts.currentSelectedAccount;

export const currentAutoPay = createSelector(
  [selectAutoPay, currentSelectedAccount],
  (autoPayInfo, id) => {
    return autoPayInfo?.find((single) => single.accountInformationId === id);
  },
);

export const { selectAll: selectAllAccounts, selectById: selectAccountById } =
  accountEntityAdapter.getSelectors((state: RootState) => state.accounts);
